import React, { useState } from 'react';
import { Upload, FileText, X } from 'lucide-react';
import { __ } from './translations';

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg'
];

const ALLOWED_FILE_EXTENSIONS = '.pdf,.png,.jpg,.jpeg';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB limit

const ResolutionManager = ({
  currentResolution,
  resolutionFile,
  onFileSelect,
  onRemove
}) => {
  const [error, setError] = useState(null);

  const validateFile = (file) => {
    setError(null);
    
    if (!file) return null;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setError(__('Only PDF, PNG, and JPG files are allowed'));
      return null;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(__('File size must be less than 10MB'));
      return null;
    }

    return file;
  };

  const getFileIcon = (fileType) => {
    if (fileType && fileType.startsWith('image/')) {
      return '🖼️'; // Image emoji for image files
    }
    return <FileText className="w-5 h-5 text-blue-600" />;
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">{__('Company Resolution')}</h3>
      
      {/* Current Resolution Display */}
      {currentResolution && !resolutionFile && (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
          <div className="flex items-center space-x-2">
            {getFileIcon(currentResolution.type)}
            <a 
              href={currentResolution.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              {currentResolution.name}
            </a>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50"
            title={__('Remove Resolution')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* New File Selection */}
      <div className="flex items-center space-x-4">
        <input
          type="file"
          accept={ALLOWED_FILE_EXTENSIONS}
          onChange={(e) => {
            const validatedFile = validateFile(e.target.files[0]);
            if (validatedFile) {
              onFileSelect(validatedFile);
            } else {
              e.target.value = ''; // Reset input if validation fails
            }
          }}
          className="hidden"
          id="resolution-upload"
        />
        <label
          htmlFor="resolution-upload"
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
        >
          <Upload className="w-4 h-4 mr-2" />
          {currentResolution ? __('Change Resolution') : __('Upload Resolution')}
        </label>
        
        {/* New File Preview */}
        {resolutionFile && (
          <div className="flex items-center space-x-2">
            {getFileIcon(resolutionFile.type)}
            <span className="text-sm text-gray-600">{resolutionFile.name}</span>
            <button
              type="button"
              onClick={() => onFileSelect(null)}
              className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50"
              title={__('Cancel Upload')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="mt-2 text-xs text-gray-500">
        {__('Accepted file types: PDF, PNG, JPG/JPEG (Max size: 10MB)')}
      </div>
    </div>
  );
};

export default ResolutionManager;